import { Link } from 'react-router-dom';
import { Box, Grid, Typography } from '@mui/material';

interface DesignsInCollectionGridProps {
  data: {
    products: Array<{
      application: string;
      available_on_resources: boolean;
      b_trade_sync: string;
      code: string;
      collection: string;
      colour: string;
      design: string;
      diameter: string;
      id: string;
      is_published: boolean;
      item_group: string;
      primaryImage: string;
      primaryImageUrl: string;
      range: string;
      sku: string;
      web_colour: string;
      web_size: string;
      width: string;
      length_cm: string;
    }>;
    filters: {
      application: string[];
    };
    return_item_count: number;
    selection_fields: Record<string, any>;
    success: boolean;
    total_items_count: number;
  } | null | undefined;
  marginBottom?: string | number;
  marginTop?: string | number;
}

const DesignsInCollectionGrid = ({
                                   data,
                                   marginBottom,
                                   marginTop
                                 }: DesignsInCollectionGridProps) => {
  // Extract the array of products from the correct property
  const productArray = data?.products || [];

  // Filter to ensure valid items
  const filteredData = productArray.filter(item =>
    item && item.is_published !== false && item.sku && item.design
  );

  // Debug: Log the data we're working with
  console.log('Consumer DesignsInCollectionGrid received data:', filteredData.length, 'items');

  // Function to get unique products per color (one product per color, smallest size)
  const getUniqueProductsPerColor = (products: any[]) => {
    const uniqueProducts = new Map();

    products.forEach((product) => {
      const design = product.design;
      const colour = product.colour;
      const key = `${design}_${colour}`;

      if (!uniqueProducts.has(key)) {
        uniqueProducts.set(key, product);
      } else {
        // Compare sizes and keep the smaller one
        const existing = uniqueProducts.get(key);
        const existingSize = getSizeValue(existing);
        const currentSize = getSizeValue(product);

        if (currentSize < existingSize) {
          uniqueProducts.set(key, product);
        }
      }
    });

    return Array.from(uniqueProducts.values());
  };

  // Helper function to calculate size value for comparison
  const getSizeValue = (item: any) => {
    const diameter = item.diameter || '';
    const width = item.width || '';
    const length = item.length_cm || '';

    if (diameter && diameter !== '0cm') {
      return parseFloat(diameter.replace('cm', '')) || 0;
    } else if (width && length) {
      const w = parseFloat(width.replace('cm', '')) || 0;
      const l = parseFloat(length.replace('cm', '')) || 0;
      return w * l; // Use area for rectangular products
    }
    return 0;
  };

  // Group products by application and get unique products per color
  const groupedByApplication = filteredData.reduce((acc: any, item: any) => {
    const application = item.application || 'Other';
    if (!acc[application]) {
      acc[application] = [];
    }
    acc[application].push(item);
    return acc;
  }, {});

  // Get unique products per application
  Object.keys(groupedByApplication).forEach(app => {
    groupedByApplication[app] = getUniqueProductsPerColor(groupedByApplication[app]);
  });

  // Debug: Log the grouped data
  console.log('Grouped by application:', Object.keys(groupedByApplication));

  // Sort applications alphabetically
  const sortedApplications = Object.keys(groupedByApplication).sort();

  const renderProductGrid = (products: any[], applicationName: string) => {
    return (
      <Grid container spacing={'12px'} key={applicationName}>
        {products
          ?.sort((a: any, b: any) =>
            (a.design || '').toLowerCase().localeCompare((b.design || '').toLowerCase())
          )
          .map((product: any, index: number) => {
            // Construct size string based on diameter or width x length - find smallest size
            const getSmallestSizeForDesignColor = (design: string, colour: string) => {
              const matchingProducts = filteredData.filter(item =>
                item.design === design && item.colour === colour
              );

              let smallestProduct = matchingProducts[0];
              let smallestSize = getSizeValue(smallestProduct);

              matchingProducts.forEach(prod => {
                const size = getSizeValue(prod);
                if (size < smallestSize && size > 0) {
                  smallestSize = size;
                  smallestProduct = prod;
                }
              });

              return smallestProduct;
            };

            const smallestProduct = getSmallestSizeForDesignColor(product.design, product.colour);
            const sizeString = smallestProduct.diameter && smallestProduct.diameter !== '0cm'
              ? smallestProduct.diameter.trim()
              : `${smallestProduct.width?.trim() || ''}x${smallestProduct.length_cm?.trim() || ''}`;

            // CORRECT (MATCHES TRADE):
            const productUrl = `/item_group/${product.item_group}/collection/${encodeURIComponent(
              product.range
            )}/design/${encodeURIComponent(
              product.design
            )}/colour/${encodeURIComponent(
              product.colour
            )}?targetSize=${encodeURIComponent(sizeString)}&targetProduct=${encodeURIComponent(smallestProduct.sku)}`;

            return (
              <Grid key={`${applicationName}-${index}`} item xs={12} sm={6} lg={2}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    width: { xs: '100%', lg: '100%' },
                  }}
                >
                  {/* Enabled right-click native context menu targeting */}
                  <Box
                    component={Link}
                    to={productUrl}
                    data-product-image="true"
                    sx={{
                      display: 'block',
                      cursor: 'pointer',
                      height: '350px',
                      width: '100%',
                      textDecoration: 'none',
                      color: 'inherit',
                      '&:hover img': {
                        transform: 'scale(1.03)',
                      },
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={product.primaryImageUrl}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.5s ease',
                      }}
                      alt={`${product.design} in ${product.colour} Product image`}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      width: '100%',
                      flexDirection: 'column',
                    }}
                  >
                    <Typography
                      mt={1}
                      fontWeight={'600'}
                      fontSize={'18px'}
                      textTransform={'capitalize'}
                      lineHeight={'27px'}
                    >
                      {product.design}
                    </Typography>
                    <Typography
                      fontWeight={'400'}
                      fontSize={'16px'}
                      textTransform={'capitalize'}
                      lineHeight={'27px'}
                    >
                      {product.colour}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            );
          })}
      </Grid>
    );
  };

  // If no data, show a message
  if (filteredData.length === 0) {
    return (
      <Box
        sx={{
          mt: marginTop,
          mb: marginBottom,
          ml: { xs: '5%', lg: '5rem' },
          mr: { xs: '5%', lg: '5rem' },
        }}
      >
        <Typography
          variant='h4'
          mb={2}
          fontWeight='400'
          fontSize={'56px'}
        >
          {`Designs in this collection`}
        </Typography>
        <Typography mt={2} mb={4} fontSize="18px" color="text.secondary">
          No designs found in this collection.
        </Typography>
      </Box>
    );
  }

  // Show grouped view if we have multiple applications
  if (sortedApplications.length > 1) {
    return (
      <Box
        sx={{
          mt: marginTop,
          mb: marginBottom,
          ml: { xs: '5%', lg: '5rem' },
          mr: { xs: '5%', lg: '5rem' },
        }}
      >
        {sortedApplications.map((applicationName, appIndex) => (
          <Box key={applicationName} sx={{ mb: appIndex < sortedApplications.length - 1 ? 8 : 0 }}>
            <Typography
              variant='h4'
              mb={3}
              fontWeight='400'
              fontSize={'56px'}
              textTransform={'capitalize'}
            >
              {applicationName}
            </Typography>
            {renderProductGrid(groupedByApplication[applicationName], applicationName)}
          </Box>
        ))}
      </Box>
    );
  }

  // Single application - show original layout
  return (
    <Box
      sx={{
        mt: marginTop,
        mb: marginBottom,
        ml: { xs: '5%', lg: '5rem' },
        mr: { xs: '5%', lg: '5rem' },
      }}
    >
      <Typography
        variant='h4'
        mb={2}
        fontWeight='400'
        fontSize={'56px'}
      >
        {`Designs in this collection`}
      </Typography>
      {renderProductGrid(filteredData, 'single')}
    </Box>
  );
};

export default DesignsInCollectionGrid;